import React, { useEffect, useState, useCallback } from "react";
import axios from '../../../api/apiClient';
import { token, domain } from "../../Security";
import { CreateTable, GeneralMessage, useGeneralMessage } from "../../../common/APICommon"; // Added Message hooks
import { ConfirmationModal } from "../../../common/ConfirmationModal";
import { useRefetchTrigger } from '../../../hooks/useRefetchTrigger';

import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import ArticleIcon from '@mui/icons-material/Article';
import { LoadingSpinner } from "../../../common/Loading";

export const EmployeeMissingRequirements = ({
  selectedData
}) => {
  const { refetchKey } = useRefetchTrigger();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSendAllConfirm, setShowSendAllConfirm] = useState(false);

  // Message Hook
  const {
    showMessage,
    message: apiMessage,
    isSuccess: apiIsSuccess,
    showGeneralMessage
  } = useGeneralMessage(5000);

  const fetchData = useCallback(async () => {
    const apiUrl = domain + `/api/Employee/employee-missing-requirements`;
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setData(response.data);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refetchKey]);

  // --- LOGIC: Bulk Send ---
  const handleSendAllMessageClick = async () => {
    setShowSendAllConfirm(false);
    setLoading(true);

    const apiUrl = domain + "/api/EmployeeMemo"; // Existing Endpoint
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7); // Memos valid for 1 week

    try {
      // Filter only employees who actually have missing requirements
      const employeesWithMissing = data.filter(emp => emp.requirements && emp.requirements.length > 0);

      if (employeesWithMissing.length === 0) {
        showGeneralMessage("No employees have missing requirements.", false);
        setLoading(false);
        return;
      }

      // Create an array of Promises to send requests in parallel
      const requests = employeesWithMissing.map(emp => {
        const payload = generateMemoPayload(emp, today, nextWeek);
        
        return axios.post(apiUrl, payload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      });

      await Promise.all(requests);

      showGeneralMessage(`Successfully sent memos to ${employeesWithMissing.length} employees.`, true);
    } catch (error) {
      console.error("Bulk send error:", error);
      showGeneralMessage("Failed to send some memos. Please try again.", false);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (emp) => {
    setSelectedEmployee(emp);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleChildSubmit = (e) => {
    e.preventDefault();
    closeModal();
  };

  return (
    <>
      {/* Main Box */}
      <div className="!w-full xl:!w-1/2 flex flex-col bg-white shadow-md rounded-md h-[250px] p-4 box-border overflow-hidden">
        {/* Header */}
        <div className="mb-2">
          <div className="flex justify-between items-center px-2">
            <h3 className="text-sm font-semibold text-gray-700 tracking-wide">
              Missing Requirements
            </h3>

            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {data.length} employees
              </span>
              <button
                onClick={() => {
                  if(data.length > 0) setShowSendAllConfirm(true);
                  else showGeneralMessage("No employees to notify.", false);
                }}
                title="Send Memo to All"
                className="flex items-center gap-1 text-xs font-medium text-white bg-sky-600 hover:bg-sky-700 px-[8px] py-[2.5px] rounded-md transition-colors border-none shadow-sm"
              >
                <span>Notify All</span>
                <SendIcon fontSize="small" className="!text-white" />
              </button>
            </div>
          </div>
          <div className="w-full h-[1px] bg-gray-200 mt-1"></div>
        </div>

        {/* Employee List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {data.map((emp, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => openModal(emp)}
            >
              <div className="flex items-baseline space-x-2">
                <span className="font-medium text-sm text-gray-800">
                  {emp.fullName}
                </span>
                <span className="text-xs text-gray-500 tracking-wide">
                  ({emp.employeeId})
                </span>
              </div>

              <span
                className={`text-sm font-semibold px-3 py-1 rounded-full
                    ${emp.requirements.length === 0
                    ? "bg-green-100 text-green-600" : emp.requirements.length === 1
                      ? "bg-yellow-100 text-yellow-600" : emp.requirements.length === 2
                        ? "bg-orange-100 text-orange-600" : "bg-red-100 text-red-600"
                  }
                `}
              >
                {emp.requirements.length} Missing
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Reusable Modal Component */}
      <RequirementDetails
        isOpen={isModalOpen}
        onClose={closeModal}
        data={selectedEmployee}
        handleSubmit={handleChildSubmit}
        showGeneralMessage={showGeneralMessage} // Pass message handler down
        setParentLoading={setLoading}
      />

      <ConfirmationModal
        isOpen={showSendAllConfirm}
        onClose={() => setShowSendAllConfirm(false)}
        onConfirm={handleSendAllMessageClick}
        title="Send Bulk Memos"
        message={`Are you sure you want to send a missing requirements memo to ALL ${data.length} listed employees?`}
        confirmText="Send All"
        cancelText="Cancel"
      />
      
      <GeneralMessage
        form={showMessage}
        message={apiMessage}
        isSuccess={apiIsSuccess}
        duration={5000}
      />

      <LoadingSpinner loading={loading} />
    </>
  );
};

// Helper function to generate standardized HTML content
const generateMemoPayload = (emp, dateFrom, dateTo) => {
  const count = emp.requirements.length;
  
  // Generate HTML List
  const listHtml = emp.requirements
    .map(req => `<li>${req.onBoardingRequirementsDescription || req.description || "Required Document"}</li>`)
    .join('');

  const description = `
    <p>Dear ${emp.fullName},</p>
    <p>This is a reminder that you still have <strong>${count}</strong> pending requirement(s) for your employment file.</p>
    <p><strong>Missing Items:</strong></p>
    <ul>${listHtml}</ul>
    <p>Please submit these requirements to HR as soon as possible.</p>
    <br/>
    <p>Thank you,</p>
    <p>Human Resources</p>
  `;

  return {
    employeePersonalInformationId: emp.employeePersonalInformationId ?? emp.id, // Fallback if ID naming differs
    title: "Notice: Missing Employment Requirements",
    description: description,
    dateFrom: dateFrom,
    dateTo: dateTo,
    content: null // No image attachment for this text-based memo
  };
};

export const RequirementDetails = ({
  isOpen,
  onClose,
  data,
  handleSubmit,
  showGeneralMessage,
  setParentLoading
}) => {

  // Table /////////////////////////////////////////////
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const headers = ['Requirement', 'File', 'Has Completed?', 'Is Active?'];
  const accessors = ['onBoardingRequirementsDescription', 'fileName', 'hasCompleted', 'isActive'];

  // Modal State
  const [showConfirm, setShowSendMemoConfirm] = useState(false);
  const [showSendTextMessage, setShowSendTextMessage] = useState(false);

  // --- LOGIC: Send Single Memo ---
  async function handleSendMemo() {
    setShowSendMemoConfirm(false);
    if(setParentLoading) setParentLoading(true);

    const apiUrl = domain + "/api/EmployeeMemo";
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    try {
      const payload = generateMemoPayload(data, today, nextWeek);

      await axios.post(apiUrl, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if(showGeneralMessage) showGeneralMessage("Memo sent successfully!", true);
    } catch (error) {
      console.error("Error sending memo:", error);
      if(showGeneralMessage) showGeneralMessage("Failed to send memo.", false);
    } finally {
      if(setParentLoading) setParentLoading(false);
    }
  }

  // --- LOGIC: SMS Notification (Placeholder/Future) ---
  function handleSendNotification() {
    setShowSendTextMessage(false);
    alert("SMS Notification functionality is not yet connected to the backend.");
  }


  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[90%] md:w-[750px] max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div className="leading-tight py-2">
            <h3 className="text-xl font-semibold text-gray-800 m-0 p-0 leading-snug">
              Requirements Status
            </h3>
            <p className="text-sm text-gray-500 mt-[2px] m-0 p-0">
              {data.fullName} <span className="text-gray-400 font-medium">({data.employeeId})</span>
            </p>
          </div>
          {/* Right side: Close button */}
          <CloseIcon
            onClick={onClose}
            className="cursor-pointer text-gray-400 hover:text-gray-600 transition-colors duration-150"
          />
        </div>

        <div className="mt-2 px-4">
          <CreateTable
            tableName={"EmployeeMissingRequirements"}
            headers={headers}
            accessors={accessors}
            data={data?.requirements || []}
            actions={[]}
            emptyMessage="No requirements found"

            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={data?.requirements?.length || 0}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t px-4 py-2 gap-1">
          <div className="flex justify-end border-t px-4 py-2 gap-1">
            <button
              onClick={() => setShowSendMemoConfirm(true)}
              disabled={data?.requirements?.length === 0}
              title={`Send ${data.fullName} a memo regarding missing items`}
              className={`flex items-center gap-1 text-xs font-medium text-white px-[8px] py-[2.5px] rounded-md transition-colors border-none shadow-sm ${data?.requirements?.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-sky-600 hover:bg-sky-700"}`}
            >
              <span>Send Memo</span>
              <ArticleIcon fontSize="small" className="!text-white" />
            </button>

            <button
              onClick={() => {
                setShowSendTextMessage(true);
              }}
              title={`Notify ${data.fullName} via text message.`}
              className="flex items-center gap-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 px-[8px] py-[2.5px] rounded-md transition-colors border-none shadow-sm"
            >
              <span>SMS Notify</span>
              <SendIcon fontSize="small" className="!text-white" />
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirm}
        onClose={() => setShowSendMemoConfirm(false)}
        onConfirm={handleSendMemo}
        title="Confirm Memo"
        message={`Are you sure you want to send a specific memo to ${data.fullName} regarding their ${data.requirements.length} missing requirements?`}
        confirmText="Send Memo"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showSendTextMessage}
        onClose={() => setShowSendTextMessage(false)}
        onConfirm={handleSendNotification}
        title="Send SMS"
        message="Are you sure you want to send a text message this employee?"
        confirmText="Proceed"
        cancelText="Cancel"
      />

    </div>
  );
};